import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface UserAttribute {
  att_name: string;
  att_value: string | boolean;
}

export interface UserInfo {
  id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  userType?: number; // Moved from registration
}

type ParsedAttributeValue = string | boolean | number;

interface UserState {
  userInfo: UserInfo;
  user_attributes: Record<string, ParsedAttributeValue>;

  setUserInfo: (info: UserInfo) => void;
  setAttributes: (user_attributes: UserAttribute[]) => void;
  updateAttribute: (name: string, value: string | boolean) => void;

  getUserType: () => string | null;
  isAdmin: () => boolean;
  isTeacher: () => boolean;
  isStudent: () => boolean;
  isAuthenticated: () => boolean;

  resetUser: () => void;
}

const parseValue = (value: string | boolean): ParsedAttributeValue => {
  if (value === "true") return true;
  if (value === "false") return false;
  if (typeof value === "string" && !isNaN(Number(value))) return Number(value);
  return value;
};

export const useUserStore = create(
  persist<UserState>(
    (set, get) => ({
      userInfo: {},
      user_attributes: {},

      setUserInfo: (info) =>
        set((state) => ({
          userInfo: {
            ...state.userInfo,
            ...info,
          },
        })),

      setAttributes: (user_attributes) => {
        const flat: Record<string, ParsedAttributeValue> = {};
        if (Array.isArray(user_attributes)) {
          user_attributes.forEach(attr => {
            flat[attr.att_name] = parseValue(attr.att_value);
          });
        }
        set({ user_attributes: flat });
      },
      

      updateAttribute: (name, value) => {
        const parsed = parseValue(value);
        set((state) => ({
          user_attributes: {
            ...state.user_attributes,
            [name]: parsed,
          },
        }));
      },

      getUserType: () => {
        const userType = get().user_attributes["user_type"];
        return typeof userType === "string" ? userType : userType?.toString() ?? null;
      },

      isAdmin: () => get().getUserType() === "1",
      isTeacher: () => get().getUserType() === "2",
      isStudent: () => get().getUserType() === "3",

      isAuthenticated: () => !!get().userInfo.email,

      resetUser: () => set({ userInfo: {}, user_attributes: {} }),
    }),
    {
      name: "dreambook-users",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
